import React, { useMemo, useState, useEffect } from "react";
import { useDates } from "dates/DatesContext";
import { useLocation } from "react-router-dom";
import { DatesProvider } from "dates/DatesContext";
import { Row, Col, Table, Card, FormCheck } from "react-bootstrap";
import moment from "moment";
import BreadcrumbDates, { type Breadcrumb } from "components/core/blocks/BreadcrumbDates";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "utils/dashboardLinks";
import { useModuleEnabled } from "redux/selectors/modules";
import checkTierAccess from "utils/checkTierAccess";
import PaymentDiscovery from "components/custom/onboarding/subscription/PaymentDiscovery";
import EmptyState from "components/custom/merchandise/EmptyState";
import { useAppSelector } from "../../redux/hooks";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { useFetch } from "hooks/api";
import { keepPreviousData } from "@tanstack/react-query";
import { formatNumber, formatCurrency, formatPercent } from "utils/formatNumber";
import { ButtonGroup, type ButtonDefinition } from "components/core/basic/ButtonGroup";
import { Flex } from "../../../styled-system/jsx";
import { trellisPalette } from "components/custom/analytics/palettes";
import Highcharts, { type SeriesSankeyPointOptionsObject } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Loading from "components/core/blocks/Loading";
import { toTitleCase } from "utils/formatText";
import { useCategoriesQuery } from "../../hooks/useCategories";
import { CategorySelectorModal } from "../categories/CategorySelector";
import { Tooltip, TooltipTrigger } from "react-aria-components";
import { Button } from "../../components/core/basic/Button";
import { css } from "../../../styled-system/css";
import { useModal } from "@ebay/nice-modal-react";

const getDashboardTo = (category?: string) => {
  return category ? `/user/_pricing/dashboard/category/${category}` : `/user/_pricing/dashboard`;
};

type InventoryBuckets = "overstock" | "healthy" | "excess";
type Buckets = "count" | "units" | "revenue";

const inventoryBucketValues: InventoryBuckets[] = ["healthy", "excess", "overstock"];

interface InventoryStats {
  report_date: string;
  health: InventoryBuckets;
  count: number;
  forecast_units: number;
  total_units: number;
  forecast_revenue: number;
  total_sales: number;
  cost: number;
}

interface GroupedStats {
  [report_date: string]: {
    health: Record<
      InventoryBuckets,
      {
        count: number;
        forecast_units: number;
        total_units: number;
        forecast_revenue: number;
        total_sales: number;
        cost: number;
      }
    >;
  };
}

interface PlanData {
  report_date: string;
  health: string;
  plan_id: number;
  plan_name: string;
  category_id: number;
}

const percentFormat = (value: number) => {
  return formatPercent(value, 2);
};

const CountCell = ({
  bucketData,
}: {
  bucketData:
    | {
        count: number;
        forecast_units: number;
        total_units: number;
        forecast_revenue: number;
        total_sales: number;
        cost: number;
      }
    | undefined;
}) => {
  const value = bucketData ? bucketData.count : 0;
  return <td className="text-center">{value}</td>;
};

const UnitCell = ({
  bucketData,
}: {
  bucketData:
    | {
        count: number;
        forecast_units: number;
        total_units: number;
        forecast_revenue: number;
        total_sales: number;
        cost: number;
      }
    | undefined;
}) => {
  const diff = bucketData ? bucketData.total_units - bucketData.forecast_units : 0;
  const diffPercent = bucketData ? diff / bucketData.total_units : 0;
  const diffPercentText = diff > 0 ? `+${percentFormat(diffPercent)}` : percentFormat(diffPercent);
  return (
    <td className="text-center">
      <div>
        <span className="pe-1">{formatNumber(bucketData ? bucketData.total_units : 0)}</span>
        <small>({formatNumber(bucketData ? bucketData.forecast_units : 0)})</small>
      </div>
      <div className="text-muted">
        <small>
          <span className="fw-bold pe-3" style={{ color: diffPercent > 0 ? trellisPalette[12] : trellisPalette[13] }}>
            {diffPercentText}
          </span>
        </small>
      </div>
    </td>
  );
};

const RevenueCell = ({
  bucketData,
  marketPlace,
}: {
  bucketData:
    | {
        count: number;
        forecast_units: number;
        total_units: number;
        forecast_revenue: number;
        total_sales: number;
        cost: number;
      }
    | undefined;
  marketPlace: string;
}) => {
  const diff = bucketData ? bucketData.total_sales - bucketData.forecast_revenue : 0;
  const diffPercent = bucketData ? diff / bucketData.total_sales : 0;
  const diffPercentText = diff > 0 ? `+${percentFormat(diffPercent)}` : percentFormat(diffPercent);
  return (
    <td className="text-center">
      <div>
        <span className="pe-1">
          {formatCurrency(bucketData ? bucketData.total_sales : 0, marketPlace, false, false, true)}
        </span>
        <small>({formatCurrency(bucketData ? bucketData.forecast_revenue : 0, marketPlace, false, false, true)})</small>
      </div>
      <div className="text-muted">
        <small>
          <span className="fw-bold pe-3" style={{ color: diffPercent > 0 ? trellisPalette[12] : trellisPalette[13] }}>
            {diffPercentText}
          </span>
        </small>
      </div>
    </td>
  );
};

const SankeyChart = ({
  leftDate,
  rightDate,
  marketPlace,
  selectedCategories,
}: {
  leftDate: string;
  rightDate: string;
  marketPlace: string;
  selectedCategories: number[];
}) => {
  const filters = {
    mp: marketPlace,
    left_date: leftDate,
    right_date: rightDate,
    category_ids: selectedCategories.length > 0 ? selectedCategories.join(",") : null,
  };

  const sankeyData = useFetch<SeriesSankeyPointOptionsObject[]>(
    ["inventory_sankey", marketPlace, leftDate, rightDate],
    `/pricing/inventory_sankey`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const nodes = [];
  for (let i = 0; i < inventoryBucketValues.length; i++) {
    const bucket = inventoryBucketValues[i];
    let color = trellisPalette[0];
    if (bucket === "healthy") {
      color = trellisPalette[4]; // good
    }
    if (bucket === "excess") {
      color = trellisPalette[7];
    }
    if (bucket === "overstock") {
      color = trellisPalette[10]; // good
    }
    const nodeLeft = {
      id: `${bucket}_${leftDate}`,
      name: toTitleCase(bucket),
      color: color,
      offset: -30,
    };
    nodes.push(nodeLeft);
    const nodeRight = {
      id: `${bucket}_${rightDate}`,
      name: toTitleCase(bucket),
      color: color,
      offset: -30,
    };
    nodes.push(nodeRight);
  }

  if (sankeyData.isLoading) {
    return <Loading fullPage={false} height={300} />;
  }

  const options: Highcharts.Options = {
    chart: {
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "20px",
      },
      height: 600,
    },
    title: {
      text: undefined,
    },
    series: [
      {
        type: "sankey",
        keys: ["from", "to", "weight"],
        nodes: nodes,
        data: sankeyData.data,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const ColumnChart = ({
  dashboardMetrics,
  selectedBucket,
}: {
  dashboardMetrics: GroupedStats;
  selectedBucket: string;
}) => {
  let bucketName: string = "count";
  if (selectedBucket === "units") {
    bucketName = "total_units";
  }
  if (selectedBucket === "revenue") {
    bucketName = "total_sales";
  }

  const dates = Object.keys(dashboardMetrics).sort();

  const series = ["healthy", "excess", "overstock"].map((healthType, idx) => {
    return {
      name: healthType.charAt(0).toUpperCase() + healthType.slice(1),
      color: trellisPalette[idx],
      data: dates.map(
        (date) =>
          dashboardMetrics[date]?.health[healthType as keyof (typeof dashboardMetrics)[string]["health"]][
            bucketName as keyof (typeof dashboardMetrics)[string]["health"]["healthy"]
          ] || 0,
      ),
      stack: "health",
    };
  });

  const options = {
    chart: {
      type: "column",
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
      height: 300,
    },
    title: {
      text: undefined,
    },
    xAxis: {
      categories: dates,
      crosshair: true,
      accessibility: {
        description: "Report Dates",
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: undefined,
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
      },
    },
    series,
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

function Dashboard() {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { group, start, end } = useDates();
  const user = useAppSelector((state) => state.user);
  const [selectedBucket, setSelectedBucket] = useState<Buckets>("count");
  const [leftDate, setLeftDate] = useState<string | null>(null);
  const [rightDate, setRightDate] = useState<string | null>(null);
  const [dates, setDates] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const categorySelectorModal = useModal(CategorySelectorModal);

  const buttunBuckets: ButtonDefinition<Buckets>[] = [
    { label: "Count", value: "count" },
    { label: "Units", value: "units" },
    { label: "Revenue", value: "revenue" },
  ];

  const filters = {
    mp: marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    category_ids: selectedCategories.length > 0 ? selectedCategories.join(",") : null,
  };

  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");
  const breadCrumbItems: Breadcrumb[] = [
    {
      name: "Pricing Dashboard",
      href: getDashboardTo(),
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: "Dashboards",
    },
  ];

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  const showEmpty = useMemo(() => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      } else {
        return "COLLECTING_DATA";
      }
    }
    return "NO_DATA";
  }, [user, marketPlace]);

  const dashboardData = useFetch<{ metrics: InventoryStats[]; plan_id: PlanData[] }>(
    ["inventory_dashboard", marketPlace, start, end],
    `/pricing/inventory_dashboard`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !showEmpty,
    },
  );

  const {
    dashboardMetrics,
    categoryCount,
  }: {
    dashboardMetrics: GroupedStats;
    categoryCount: Record<number, number>;
  } = useMemo(() => {
    if (!dashboardData.data) {
      const dashboardMetrics: GroupedStats = {};
      const categoryCount: Record<number, number> = {};
      return {
        dashboardMetrics: dashboardMetrics,
        categoryCount: categoryCount,
      };
    }
    const categoryCount: Record<number, number> = {};
    const dashboardMetrics: GroupedStats = dashboardData.data.metrics.reduce(
      (acc: GroupedStats, item: InventoryStats) => {
        const { report_date, health, count, forecast_units, total_units, forecast_revenue, total_sales, cost } = item;

        if (!acc[report_date]) {
          acc[report_date] = {
            health: {} as Record<
              InventoryBuckets,
              {
                count: number;
                forecast_units: number;
                total_units: number;
                forecast_revenue: number;
                total_sales: number;
                cost: number;
              }
            >,
          };
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!acc[report_date].health[health]) {
          acc[report_date].health[health] = {
            count: 0,
            forecast_units: 0,
            total_units: 0,
            forecast_revenue: 0,
            total_sales: 0,
            cost: 0,
          };
        }

        acc[report_date].health[health].count += count;
        acc[report_date].health[health].forecast_units += forecast_units;
        acc[report_date].health[health].total_units += total_units;
        acc[report_date].health[health].forecast_revenue += forecast_revenue;
        acc[report_date].health[health].total_sales += total_sales;
        acc[report_date].health[health].cost += cost;

        return acc;
      },
      {},
    );

    const op = dashboardData.data.plan_id;
    op.forEach((plan) => {
      if (categoryCount[plan.category_id]) {
        categoryCount[plan.category_id] = (categoryCount[plan.category_id] ?? 0) + 1;
      } else {
        categoryCount[plan.category_id] = 1;
      }
    });

    return { dashboardMetrics, categoryCount };
  }, [dashboardData]);

  useEffect(() => {
    if (dates.length === 2) {
      //   sort dates using moment
      const sortedDates = dates.sort((a, b) => moment(a).diff(moment(b)));
      setLeftDate(sortedDates[0] ?? null);
      setRightDate(sortedDates[1] ?? null);
    } else {
      setLeftDate(null);
      setRightDate(null);
    }
  }, [dates]);

  if (showEmpty) {
    return <EmptyState type={showEmpty} />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} hideCompare hideGroupBy></BreadcrumbDates>
      <PageContainer withBreadcrumb padding={"large"}>
        <Flex gap="small" justifyContent={"space-between"} alignContent={"center"} paddingBottom={"medium"} wrap="wrap">
          {!dashboardData.isLoading && (
            <ButtonGroup
              buttons={buttunBuckets}
              value={selectedBucket}
              onSelect={(values) => {
                //   setSelectedPlanStrategy(values);
                setSelectedBucket(values);
              }}
            />
          )}

          <CategorySelectorButton
            onClick={() => {
              void categorySelectorModal.show({
                initialSelectedCategories: selectedCategories,
                onConfirm: (value) => {
                  setSelectedCategories(value);
                },

                getIrrelevantCategoriesHiddenText: (num) => `${num} categories without plans not shown`,
                sortCategories: (a, b) => {
                  const aCnt = categoryCount[a.id] ?? 0;
                  const bCnt = categoryCount[b.id];
                  if (aCnt && bCnt) {
                    return bCnt - aCnt;
                  } else if (aCnt) {
                    return -1;
                  } else if (bCnt) {
                    return 1;
                  } else {
                    return 0;
                  }
                },
                hideIrrelevantCategories: (category) => {
                  if (categoryCount[category.id]) {
                    return false;
                  }
                  return true;
                },
                getDescription: (category) => {
                  if (categoryCount[category.id]) {
                    return `Number of plans: ${categoryCount[category.id]}`;
                  }
                  return "";
                },
              });
            }}
            selectedCategories={selectedCategories}
          />
        </Flex>
        <Row>
          <Col className="ph-4 mb-3 fs-standard" xs={12}>
            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body>
                <Row>
                  <Col className="mb-3" xs={12}>
                    <div className="py-2 px-3">
                      <div className="fw-bold fs-3">Inventory Health</div>
                      <div className="fs-4">
                        This table summarizes inventory health metrics, showing the <strong>{selectedBucket}</strong> of
                        items categorized as Healthy, Excess, or Overstock for the selected date range.
                      </div>
                    </div>
                  </Col>
                  <Col xxl={6} className="px-4" xs={12}>
                    <Table striped borderless hover>
                      <thead>
                        <tr>
                          <th className="text-start">Date</th>
                          <th>Healthy</th>
                          <th>Excess</th>
                          <th>Overstock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(dashboardMetrics).map(([date, healthData]) => {
                          return (
                            <tr key={date}>
                              <td>
                                <FormCheck
                                  type="checkbox"
                                  id={date}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const d: string[] = [...dates];
                                      d.push(date);
                                      setDates(d);
                                    } else {
                                      const d: string[] = [...dates];
                                      const index = d.indexOf(date);
                                      if (index > -1) {
                                        d.splice(index, 1);
                                      }
                                      setDates(d);
                                    }
                                  }}
                                  label={moment(date).format("MMMM DD YYYY")}
                                  disabled={!!(dates.length >= 2 && !dates.includes(date))}
                                  checked={dates.includes(date)}
                                />
                              </td>
                              {inventoryBucketValues.map((bucket) => {
                                const bucketData = healthData.health[bucket];
                                return (
                                  <React.Fragment key={bucketData.count}>
                                    {selectedBucket === "count" && <CountCell bucketData={bucketData} />}
                                    {selectedBucket === "units" && <UnitCell bucketData={bucketData} />}
                                    {selectedBucket === "revenue" && (
                                      <RevenueCell bucketData={bucketData} marketPlace={marketPlace} />
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Col>
                  <Col xxl={6} className="pt-3 px-4" xs={12}>
                    <ColumnChart dashboardMetrics={dashboardMetrics} selectedBucket={selectedBucket} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col className="ph-4 mb-3 fs-standard" xs={12}>
            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body>
                <div className="py-2 px-3">
                  <div className="fw-bold fs-3">Inventory Evolution</div>
                  <div className="fs-4">
                    This chart visualizes the flow of inventory health metrics, showing the relationships between
                    different inventory health metrics.
                  </div>
                </div>
                {leftDate && rightDate && (
                  <SankeyChart
                    leftDate={leftDate}
                    rightDate={rightDate}
                    marketPlace={marketPlace}
                    selectedCategories={selectedCategories}
                  />
                )}

                {(!leftDate || !rightDate) && (
                  <div className="text-center py-5 fs-3 fw-bold">
                    Select 2 dates to view the inventory health evolution.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </PageContainer>
    </>
  );
}

const InventoryDashboard = () => {
  const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");

  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);

  const location = useLocation<{ firstVisit?: boolean; onboardingComplete?: boolean } | undefined>();

  const DYNAMIC_PRICING_SUBSCRIPTION =
    (((user.pricing_tier && user.pricing_tier !== "unknown") ||
      location.state?.firstVisit ||
      location.state?.onboardingComplete) &&
      (amz_onboard.org_status === "active" ||
        amz_onboard.org_status === "trial_active" ||
        amz_onboard.org_status === "demo_org")) ||
    hasPriceOptimizationModule ||
    checkTierAccess(user.tier, "pricing", amz_onboard.org_status);

  if (!DYNAMIC_PRICING_SUBSCRIPTION) {
    // Workaround for untyped component incorrect inference
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="pricing" {...({} as any)} />;
  }

  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

function CategorySelectorButton({
  onClick,
  selectedCategories,
}: {
  onClick: () => void;
  selectedCategories: number[];
}) {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const categoryQuery = useCategoriesQuery(marketPlace, channel);
  return (
    <TooltipTrigger>
      <Button onPress={onClick} variant={"primary"}>
        {`Select Categories ${selectedCategories.length > 0 ? `(${selectedCategories.length})` : ""}`}
      </Button>
      <Tooltip
        placement={"bottom"}
        className={css({
          border: "1px solid {colors.lightgrey}",
          borderRadius: "light",
          padding: "medium",
          fontSize: "medium",
          backgroundColor: "white",
        })}
        offset={10}
      >
        {selectedCategories.length ?
          <figure>
            <figcaption>Selected Categories:</figcaption>
            <ul>
              {categoryQuery.data
                ?.filter((c) => selectedCategories.includes(c.id))
                .map((category) => <li key={category.id}>{category.leaf_category_name}</li>)}
            </ul>
          </figure>
        : <p
            className={css({
              margin: "0",
              verticalAlign: "middle",
            })}
          >
            Showing all categories
          </p>
        }
      </Tooltip>
    </TooltipTrigger>
  );
}

export default InventoryDashboard;
